<script setup lang="ts">
import { authApi } from "@/api/authApi";
import { useCookieTracking } from "@/composables/useCookieTracking";
import { useDataLayer } from "@/composables/useDataLayer";
import { useModalManager } from "@/composables/useModalManager";
import router from "@/router";
import { useAuthenticationStore } from "@/stores/useAuthenticationStore";
import { AuthSectionEnum } from "@/types/enums";
import { handleError } from "@/utils/errorHelper";
import useVuelidate from "@vuelidate/core";
import { minLength, required } from "@vuelidate/validators";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

const props = defineProps<{
  emailExists: boolean;
  emailAddress: string;
}>();

const { getTrackingCookies } = useCookieTracking();
const { pushToDataLayer } = useDataLayer();
const { removeRedirectUrl, getRedirectUrl, executePendingActions } = useModalManager();
const authStore = useAuthenticationStore();
const route = useRoute();
const loading = ref(false);
const error = ref("");
const password = ref("");

const rules = computed(() => ({
  password: { required, minLength: minLength(8) },
}));
const v$ = useVuelidate(rules, { password });

const passwordErrors = computed(() => {
  const passwordValidator = v$.value.password as any;
  if (passwordValidator?.$dirty && passwordValidator.$invalid) {
    if (passwordValidator.required?.$invalid) return "Password is required.";
    if (passwordValidator.minLength?.$invalid)
      return "Password must be at least 8 characters long.";
  }
  return null;
});

const submit = async () => {
  const passwordValid = await v$.value.$validate();
  if (!passwordValid) return;

  loading.value = true;
  try {
    const trackingCookies = getTrackingCookies();
    trackingCookies.client_ip = authStore.userIp;

    const response = props.emailExists
      ? await authApi.login({ email: props.emailAddress, password: password.value })
      : await authApi.register({
          email: props.emailAddress,
          password: password.value,
          cookies: trackingCookies,
        });

    if (response.data.access_token && response.data.user) {
      authStore.login(response.data.user, response.data.access_token);
      if (!props.emailExists) {
        pushToDataLayer({
          event: "Register",
          eventCategory: "Register",
          eventAction: "submit",
          eventLabel: "RegisterForm",
        });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "sign_up_fe" });
      }
      await afterAction();
      emit("update:show", false);
    }
  } catch (responseError) {
    error.value = handleError(responseError);
  } finally {
    loading.value = false;
  }
};

const afterAction = async () => {
  const redirect = getRedirectUrl();

  if (redirect) {
    const resolved = router.resolve(redirect);

    removeRedirectUrl();
    await router.push({
      path: resolved.path,
      query: route.query,
    });
  }
  executePendingActions();
};

const goToForgotPassword = async () => {
  if (props.emailAddress) {
    emit("update:activeSection", AuthSectionEnum.ForgotPassword);
    emit("update:header", "Recover your password");
  }
};

const resetModal = () => {
  emit("update:activeSection", AuthSectionEnum.Email);
  emit("update:emailExists", false);
  emit("update:header", "Welcome to 4C Predictions");
};

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "update:activeSection", value: AuthSectionEnum): void;
  (e: "update:emailExists", value: boolean): void;
  (e: "update:header", value: string): void;
}>();
</script>

<template>
  <form @submit.prevent="submit">
    <div class="flex items-center justify-between">
      <Button
        @click="resetModal"
        type="button"
        class="group border-transparent bg-transparent !p-0"
        aria-label="Go back to email input"
      >
        <i class="pi pi-arrow-left text-sm text-slate-500 group-hover:text-black"></i>
        <span class="text-[15px] font-medium text-slate-500 group-hover:text-black">Back</span>
      </Button>
      <Button
        v-if="emailExists"
        @click="goToForgotPassword"
        variant="text"
        type="button"
        label="Forgot password?"
        class="!p-0 hover:bg-transparent"
      >
        <span class="text-[15px] font-medium text-slate-500 hover:underline">Forgot password?</span>
      </Button>
    </div>
    <Password
      v-model="password"
      @blur="v$.password.$touch()"
      :class="{ 'p-invalid': v$.password.$invalid && v$.password.$dirty }"
      toggleMask
      :placeholder="emailExists ? 'Enter your password' : 'Enter account password'"
      :feedback="false"
      class="mt-2 w-full"
      autofocus
    />
    <div v-if="passwordErrors || error" class="p-error">
      {{ passwordErrors ?? error }}
    </div>
    <Button type="submit" :disabled="loading" class="mt-4 w-full !px-8">
      <i v-if="loading" class="pi pi-spin pi-spinner"></i>
      <span class="text-[15px] font-medium text-black">
        {{ emailExists ? "Sign In" : "Join Now" }}
      </span>
    </Button>
  </form>
</template>
